import { CalendarRange, RotateCcw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ReportingBucketSize } from '@/api/model'
import { Button } from '@/components/shadcn/button'
import { Card, CardContent } from '@/components/shadcn/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { FilterRowChartItem } from '@/components/common/filters/FilterRowChartItem'
import { FilterDatePicker } from '@/components/common/filters/FilterDatePicker'
import { BUCKET_SIZE_OPTIONS } from '../charts/bucketLabels'
import { defaultDashboardDateFilter, earliestStartDate, type DashboardDateFilter, type DateFilterError } from '../dateFilter'

interface Props {
  value: DashboardDateFilter
  error: DateFilterError | null
  onChange: (value: DashboardDateFilter) => void
}

export function DashboardDateFilterBar({ value, error, onChange }: Props) {
  const { t } = useTranslation(['dashboard', 'filters'])
  // Same values as on page load
  const defaults = defaultDashboardDateFilter()
  // The calendar only offers days the backend accepts; typed dates are still checked by the validation
  const earliest = earliestStartDate(defaults.endDate)

  function resetButton(isDefault: boolean, onReset: () => void) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="outline" size="icon" className="h-9 w-9" disabled={isDefault} onClick={onReset}>
            <RotateCcw className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('filters:dropdown.reset')}</TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-2">
        <div className="flex flex-row flex-wrap items-center gap-x-10 gap-y-3">
          <div className="flex items-center gap-2 font-semibold">
            <CalendarRange className="size-5 text-primary" />
            {t('dateFilter.title')}
          </div>
          <FilterRowChartItem label={t('dateFilter.bucketSize')}>
            <Select
              value={value.bucketSize}
              onValueChange={(bucketSize) => onChange({ ...value, bucketSize: bucketSize as ReportingBucketSize })}
            >
              <SelectTrigger className="text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BUCKET_SIZE_OPTIONS.map((size) => (
                  <SelectItem key={size} value={size}>{t(`dateFilter.bucketSizes.${size}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterRowChartItem>
          <FilterRowChartItem label={t('dateFilter.startDate')}>
            <FilterDatePicker
              value={value.startDate}
              minDate={earliest}
              maxDate={value.endDate || defaults.endDate}
              onChange={(startDate) => onChange({ ...value, startDate })}
            />
            {resetButton(value.startDate === defaults.startDate,
              () => onChange({ ...value, startDate: defaults.startDate }))}
          </FilterRowChartItem>
          <FilterRowChartItem label={t('dateFilter.endDate')}>
            <FilterDatePicker
              value={value.endDate}
              minDate={value.startDate || earliest}
              maxDate={defaults.endDate}
              onChange={(endDate) => onChange({ ...value, endDate })}
            />
            {resetButton(value.endDate === defaults.endDate,
              () => onChange({ ...value, endDate: defaults.endDate }))}
          </FilterRowChartItem>
        </div>
        {error && <p className="text-sm text-destructive">{t(`dateFilter.errors.${error}`)}</p>}
      </CardContent>
    </Card>
  )
}
